import { motion } from 'framer-motion';
import { X, ExternalLink, ShoppingBag, Check, AlertCircle } from 'lucide-react';
import type { PurchaseLink } from '@/types';

interface ShopThisLookProps {
  links: PurchaseLink[];
  onClose: () => void;
}

export function ShopThisLook({ links, onClose }: ShopThisLookProps) {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="relative w-full max-w-lg bg-white rounded-t-3xl max-h-[80vh] overflow-hidden"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-vestir-gray rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-vestir-gray/30">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-vestir-purple" />
            <h2 className="font-display text-lg font-semibold">Shop This Look</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-vestir-gray/40 rounded-full flex items-center justify-center"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Links list */}
        <div className="px-5 py-4 overflow-y-auto max-h-[60vh] space-y-3">
          {links.length > 0 ? (
            links.map((link) => (
              <motion.a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-vestir-gray/10 rounded-2xl
                           hover:bg-vestir-gray/20 transition-colors group"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{link.retailer}</p>
                    {link.inStock ? (
                      <span className="flex items-center gap-0.5 text-[10px] text-green-600 bg-green-50
                                       px-1.5 py-0.5 rounded-full">
                        <Check size={8} /> In Stock
                      </span>
                    ) : (
                      <span className="flex items-center gap-0.5 text-[10px] text-vestir-coral bg-red-50
                                       px-1.5 py-0.5 rounded-full">
                        <AlertCircle size={8} /> Out of Stock
                      </span>
                    )}
                  </div>

                  <p className="text-lg font-bold text-vestir-purple mt-0.5">
                    {link.currency === 'USD' ? '$' : link.currency}
                    {link.price.toFixed(2)}
                  </p>

                  {/* Sizes */}
                  {link.sizes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {link.sizes.map((size) => (
                        <span
                          key={size}
                          className={`px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors ${
                            size === link.recommendedSize
                              ? 'border-vestir-purple bg-vestir-purple/10 text-vestir-purple'
                              : 'border-vestir-gray/50 text-vestir-gray-dark'
                          }`}
                        >
                          {size}
                          {size === link.recommendedSize && ' (rec)'}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="text-[10px] text-vestir-gray-dark mt-1.5">
                    Last checked: {new Date(link.lastChecked).toLocaleDateString()}
                  </p>
                </div>

                <ExternalLink
                  size={18}
                  className="text-vestir-gray-dark group-hover:text-vestir-purple transition-colors flex-shrink-0"
                />
              </motion.a>
            ))
          ) : (
            <div className="text-center py-8">
              <ShoppingBag size={32} className="mx-auto text-vestir-gray-dark/30 mb-3" />
              <p className="text-sm text-vestir-gray-dark">
                No purchase links available for this look.
              </p>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="px-5 py-3 border-t border-vestir-gray/30">
          <p className="text-[10px] text-vestir-gray-dark text-center">
            Prices and availability may vary. Vestir may earn a commission from purchases.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
